"use client";

import { useMemo } from "react";
import { useTable } from "spacetimedb/react";
import { tables } from "@/module_bindings";
import { buildTagIdsByPlaybook, mapPlaybook } from "@/lib/stdb-map";
import { useResolvedCurrentOrganization } from "./use-current-organization";
import type { GlaciPlaybook } from "@/types/glaci";

export function usePlaybooks(orgId?: string): {
  playbooks: GlaciPlaybook[];
  isReady: boolean;
} {
  const current = useResolvedCurrentOrganization();
  const effectiveOrgId = orgId ?? current.organization?.id;

  const [playbookRows, playbooksReady] = useTable(tables.org_playbooks);
  const [tagLinkRows, tagLinksReady] = useTable(tables.org_playbook_tags);

  const tagIdsByPlaybook = useMemo(() => {
    if (!effectiveOrgId) return new Map<string, string[]>();
    return buildTagIdsByPlaybook(
      tagLinkRows.filter((row) => row.organizationId === effectiveOrgId),
    );
  }, [tagLinkRows, effectiveOrgId]);

  const playbooks = useMemo(() => {
    if (!effectiveOrgId) return [];
    return playbookRows
      .filter((row) => row.organizationId === effectiveOrgId)
      .map((row) => mapPlaybook(row, tagIdsByPlaybook));
  }, [playbookRows, tagIdsByPlaybook, effectiveOrgId]);

  const isReady = orgId
    ? playbooksReady && tagLinksReady
    : playbooksReady && tagLinksReady && current.isReady;

  return { playbooks, isReady };
}
